import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {ErrorBoundary} from '@sentry/react';
import {ProfileTableHeader} from 'src/components/profile-tab-structure';
import {SectionFilter} from 'src/components/section-filter';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {NoResultPage} from 'src/modules/entitlements';
import {ErrorPage} from 'src/modules/error-page/error-page.components';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button, {FlatButton} from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';

import {useGetTrackedEmailTypeDictionary} from './hooks/useGetTrackedEmailTypeDictionary';
import {useGetUserEmailDeliveryLog} from './hooks/useGetUserEmailDeliveryLog';
import {userEmailDeliveryLogStateActions} from './slices/user-email-delivery-log-state.slice';
import {userEmailDeliveryLogActions} from './slices/user-email-delivery-log.slice';
import {EmailDeliveryHistoryTable} from './table/email-delivery-history-table';
import {EmailTypeFilter} from './table/filters/email-type-filter';
import {StatusDateFilter} from './table/filters/status-date-filter';

import type {UserEmailLogsCriteria} from './typedef';

type Props = {
    userId: number,
};

export const EmailDeliveryLog = ({userId}: Props) => {
    const dispatch = useDispatch();

    const {loadingError, result, cacheKeys} = useGetUserEmailDeliveryLog({userId});
    const {
        data: trackedEmailTypeDictionary,
        loadingError: trackedEmailTypeDictionaryLoadingError,
    } = useGetTrackedEmailTypeDictionary();

    const noUserResultMessage = useMemo(() => {
        if (Object.keys(cacheKeys.filters).length && !result?.data.length) {
            return (
                <NoResultPage
                    title="No data found for the selected criteria."
                    description=""
                />
            );
        } else if (!Object.keys(cacheKeys.filters).length) {
            return (
                <NoResultPage
                    title="No data to show."
                    description=""
                />
            );
        }

        return <div/>;
    }, [cacheKeys.filters, result]);

    const applyFilters = useCallback((filters: UserEmailLogsCriteria) => {
        dispatch(userEmailDeliveryLogStateActions.setFilters(filters));
    }, [dispatch]);

    const clearFilters = useCallback(() => {
        const {startDate, endDate, status, ...otherFilters} = cacheKeys.filters;
        dispatch(userEmailDeliveryLogStateActions.setFilters(otherFilters));
        return otherFilters;
    }, [cacheKeys.filters, dispatch]);

    const clearAllFilters = useCallback(() => {
        dispatch(userEmailDeliveryLogStateActions.setFilters({}));
    }, [dispatch]);

    useEffect(() => {
        return () => {
            dispatch(userEmailDeliveryLogActions.clearData());
            dispatch(userEmailDeliveryLogStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
            <ServerResponseHandler
                serverError={loadingError || trackedEmailTypeDictionaryLoadingError}
                sectionName="User Email Delivery History">
                <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                    <BoxLoading loading={!result}>
                        <ProfileTableHeader
                            caption="User Email Delivery History"
                            totalCount={result?.totalCount}
                            entityCaption="Action">
                            {!(!result?.totalCount && !Object.keys(cacheKeys.filters).length) ? (
                                <Group direction="h" gap="m" as="section">
                                    <EmailTypeFilter
                                        trackedEmailType={trackedEmailTypeDictionary}
                                        filter={cacheKeys.filters as UserEmailLogsCriteria}
                                        onChange={newFilter =>
                                            dispatch(userEmailDeliveryLogStateActions.setFilters(newFilter))}
                                    />
                                    <SectionFilter<UserEmailLogsCriteria>
                                        onApply={applyFilters}
                                        onClear={clearFilters}
                                        filters={cacheKeys.filters as UserEmailLogsCriteria}
                                        getAnchor={({anchorRef, positionRef, getAnchorProps}) => {
                                            const count = Object
                                                .keys(cacheKeys.filters as UserEmailLogsCriteria)
                                                .filter(key => key !== 'email')
                                                .filter(Boolean).length;

                                            const filterIconType = count ? Icons.FILTER_FILLED : Icons.FILTER_EMPTY;

                                            return (
                                                <Button
                                                    variant="Type.SECONDARY"
                                                    startIcon={<Icon type={filterIconType}/>}
                                                    endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}
                                                    ref={anchorRef}
                                                    {...getAnchorProps()}>
                                                    <b>Delivery Status & Date</b>
                                                </Button>
                                            );
                                        }}
                                        defaultFilters={{}}>
                                        {(filter, onChange) => (
                                            <StatusDateFilter
                                                filter={filter}
                                                onChange={onChange}
                                            />
                                        )}
                                    </SectionFilter>
                                    <FlatButton
                                        onClick={clearAllFilters}
                                        disabled={!Object.keys(cacheKeys.filters).length}>
                                        <b>Clear all filters</b>
                                    </FlatButton>
                                </Group>
                            ) : null}
                        </ProfileTableHeader>
                        {result?.data.length ? (
                            <div style={{minHeight: 66}}>
                                <EmailDeliveryHistoryTable
                                    rows={result?.data || []}
                                    totalCount={result?.totalCount || 0}
                                    pagination={cacheKeys.pagination}
                                    trackedEmailType={trackedEmailTypeDictionary}
                                />
                            </div>
                        ) : noUserResultMessage}
                    </BoxLoading>
                </ErrorBoundary>
            </ServerResponseHandler>
        </div>
    );
};
